import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';

import { isEqual, isNil } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { searchPartnersRequest } from '../../../redux/ducks/SearchDucks';
import type {
  PartnerResultDescriptorType,
  SearchPartnersRequestType,
} from '../../../redux/ducks/SearchDucks.types';
import type { SearchSelectionType } from '../../../redux/ducks/StorageDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import LoadingCard from '../../layout/LoadingCard/LoadingCard';
import ProfessionalCard from '../../layout/ProfessionalCard/ProfessionalCard';
import { convertToSearchType } from './WelcomeBody';

const MAX_CARDS = 10000;
const PRELOADING_THRESHOLD = 0.8;

export type WelcomeListViewProps = {
  onPartnerSelected: (partner: PartnerResultDescriptorType) => void;
  onPartnerVisibilityChange: ({
    partnerDescriptor,
    isVisible,
  }: {
    partnerDescriptor: PartnerResultDescriptorType;
    isVisible: boolean;
  }) => void;
  scrollableTarget: string;
};

const WelcomeListView: React.FC<WelcomeListViewProps> = ({
  onPartnerVisibilityChange,
  onPartnerSelected,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const searchPartnersResponse = useSelector(
    (state: RootReducerType) => state.search.searchPartnersResponse?.data,
  );
  const searchSelection = useSelector(
    (state: RootReducerType) => state.storage.searchSelection,
  );

  const [lastSearchParams, setLastSearchParams] = useState<
    SearchPartnersRequestType | undefined
  >();
  const [totalCards, setTotalCards] = useState<number>(MAX_CARDS);
  const [items, setItems] = useState<PartnerResultDescriptorType[]>([]);

  const searchSel = useMemo<SearchSelectionType | undefined>(() => {
    if (!isNil(searchSelection)) {
      return searchSelection;
    }

    return undefined;
  }, [searchSelection]);

  const createProfessionalBlock = ({
    descriptor,
    index,
    isDuplicate,
  }: {
    descriptor: PartnerResultDescriptorType;
    index: number;
    isDuplicate: boolean;
  }) => {
    if (isDuplicate) {
      return null;
    }

    return (
      <div
        id={`professional-card-${descriptor.id}`}
        data-test-id={`professional-card-${descriptor.id}`}
        key={`professional-card-${descriptor.id}`}
        className="size-full min-w-full"
      >
        <ProfessionalCard
          partnerDescriptor={descriptor}
          partnerIndex={index}
          data-test-id={`professional-test-id-${descriptor.id}`}
          onClick={() => onPartnerSelected(descriptor)}
          onVisibilityChange={({ isVisible }) =>
            onPartnerVisibilityChange({
              partnerDescriptor: descriptor,
              isVisible,
            })
          }
        />
      </div>
    );
  };

  const fetchMoreData = () => {
    if (searchSel) {
      const params = convertToSearchType(searchSel, items.length);
      if (!isEqual(params, lastSearchParams)) {
        setLastSearchParams(params); // should be set only if changed
      }
    }
  };

  useEffect(() => {
    if (searchSel) {
      const params = convertToSearchType(searchSel, 0);
      if (!isEqual(params, lastSearchParams)) {
        // setOnNewResults(true); // questa chiamata causa: Uncaught Error: Maximum update depth exceeded. This can happen when a component repeatedly calls setState inside componentWillUpdate or componentDidUpdate
        setItems([]);
        setLastSearchParams(params); // should be set only if changed
      }
    }
  }, [searchSel]);

  useEffect(() => {
    if (searchPartnersResponse?.results) {
      setTotalCards(searchPartnersResponse.total);

      const newItems: PartnerResultDescriptorType[] = [];
      newItems.push(...items);
      newItems.push(...searchPartnersResponse.results);
      // newItems.push(searchPartnersResponse.results[0]); // TEMP, should expand all results

      setItems(newItems);
      if (searchPartnersResponse.offset === 0) {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }
  }, [searchPartnersResponse]);

  useEffect(() => {
    if (lastSearchParams) {
      dispatch(searchPartnersRequest(lastSearchParams));
    }
  }, [lastSearchParams]);

  const noResults = useMemo<boolean>(
    () =>
      !isNil(searchPartnersResponse) &&
      searchPartnersResponse?.results &&
      searchPartnersResponse?.results.length === 0 &&
      !isNil(items) &&
      items.length === 0,
    [searchPartnersResponse, items],
  );

  return noResults ? (
    <div className="my-28 flex size-full flex-col items-center justify-center text-xl font-semibold text-slate-300">
      {t('static.generic.no_results')}
    </div>
  ) : (
    <InfiniteScroll
      dataLength={items.length}
      next={fetchMoreData}
      hasMore={items.length < totalCards}
      scrollThreshold={PRELOADING_THRESHOLD}
      loader={
        <>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
          <div className="size-full min-w-full">
            <LoadingCard />
          </div>
        </>
      }
      className="grid min-w-full grow grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
      style={{
        margin: 0,
        marginTop: '8px',
        paddingLeft: '8px',
        paddingRight: '8px',
        overflow: 'hidden',
        alignItems: 'center',
        justifyItems: 'center',
        minWidth: '100%',
      }}
    >
      {items?.map((descriptor, index) => {
        const isDuplicate =
          items.filter((x, idx) => x.id === descriptor.id && idx < index)
            .length > 0;
        return createProfessionalBlock({ descriptor, index, isDuplicate });
      })}
    </InfiniteScroll>
  );
};

export default WelcomeListView;
